import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export const MODULE_ID = 'mir.mod-036' as const;
export const MODULE_VERSION = '0.1.0' as const;
export const MIN_SAMPLE_RATE = 8_000;
export const MAX_SAMPLE_RATE = 96_000;
export const MIN_BLOCK_SIZE = 16;
export const MAX_BLOCK_SIZE = 8_192;
export const MAX_SOURCE_FRAMES = 57_600_000;
export const MAX_TAIL_FRAMES = 9_600_000;
export const TRUE_PEAK_OVERSAMPLE = 4;
export const TRUE_PEAK_TAPS_PER_PHASE = 32;
export const WAVE_METADATA_KEYS = Object.freeze(['IART', 'ICMT', 'ICRD', 'IGNR', 'INAM', 'IPRD', 'ISFT'] as const);

export type WaveMetadataKey = (typeof WAVE_METADATA_KEYS)[number];
export type WaveMetadata = Readonly<Partial<Record<WaveMetadataKey, string>>>;

export interface RenderBlockContext {
  readonly startFrame: number;
  readonly frames: number;
  readonly sampleRate: number;
  readonly outputs: readonly Float32Array[];
}

export type RenderBlockCallback = (context: RenderBlockContext) => void;

export interface ExactTailOptions {
  readonly mode: 'exact';
  readonly frames: number;
}

export interface AutomaticTailOptions {
  readonly mode: 'automatic';
  readonly thresholdDbfs: number;
  readonly holdFrames: number;
  readonly maxFrames: number;
}

export type TailOptions = ExactTailOptions | AutomaticTailOptions;

export interface LoopOptions {
  readonly startFrame: number;
  readonly endFrame: number;
  readonly crossfadeFrames: number;
}

export interface OfflineRenderRequest {
  readonly sampleRate: number;
  readonly channels: number;
  readonly sourceFrames: number;
  readonly blockSize: number;
  readonly renderBlock: RenderBlockCallback;
  readonly tail?: TailOptions;
  readonly loop?: LoopOptions;
  readonly metadata?: WaveMetadata;
  readonly isCancelled?: () => boolean;
}

export interface SignalAnalysis {
  readonly peak: number;
  readonly absolutePeak: number;
  readonly peakFrame: number;
  readonly rms: number;
  readonly dcMean: number;
  readonly nonFiniteCount: number;
  readonly overRangeCount: number;
  readonly truePeak4x: number;
}

export interface RenderAnalysis {
  readonly channels: readonly SignalAnalysis[];
  readonly aggregate: SignalAnalysis;
}

export type TailDecision =
  | Readonly<{ mode: 'none' }>
  | Readonly<{ mode: 'exact'; requestedFrames: number }>
  | Readonly<{
      mode: 'automatic';
      thresholdDbfs: number;
      thresholdLinear: number;
      holdFrames: number;
      maxFrames: number;
    }>;

export interface OfflineRenderResult {
  readonly sampleRate: number;
  readonly channels: number;
  readonly sourceFrames: number;
  readonly tailFrames: number;
  readonly totalFrames: number;
  readonly blockSize: number;
  readonly loopDecision: Readonly<LoopOptions> | null;
  readonly tailDecision: TailDecision;
  readonly callbackCount: number;
  /** Canonical interleaved IEEE-754 float32 render data. */
  readonly samples: Float32Array;
  readonly wavBytes: Uint8Array;
  /** Deterministic FNV-1a-64 identifier; not a cryptographic hash. */
  readonly contentHash: string;
  readonly analysis: RenderAnalysis;
  readonly metadata: WaveMetadata;
}

export type OfflineRenderErrorCode =
  | 'INVALID_REQUEST'
  | 'METADATA_INVALID'
  | 'TAIL_LOOP_CONFLICT'
  | 'TAIL_LIMIT'
  | 'RIFF_OVERFLOW'
  | 'NON_FINITE'
  | 'CANCELLED'
  | 'CALLBACK_FAILED'
  | 'SAMPLE_LAYOUT';

export class OfflineRenderError extends Error {
  readonly code: OfflineRenderErrorCode;
  readonly channel: number | undefined;
  readonly frame: number | undefined;
  readonly startFrame: number | undefined;

  constructor(
    code: OfflineRenderErrorCode,
    message: string,
    details: { channel?: number; frame?: number; startFrame?: number; cause?: unknown } = {},
  ) {
    super(`${code}: ${message}`, details.cause === undefined ? undefined : { cause: details.cause });
    this.name = 'OfflineRenderError';
    this.code = code;
    this.channel = details.channel;
    this.frame = details.frame;
    this.startFrame = details.startFrame;
  }
}

const fail = (code: OfflineRenderErrorCode, message: string): never => {
  throw new OfflineRenderError(code, message);
};

const assertInteger = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    fail('INVALID_REQUEST', `${label} must be an integer in [${minimum}, ${maximum}]`);
  }
};

const utf8 = new TextEncoder();
const validUnicode = (value: string): boolean => {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code >= 0xd800 && code <= 0xdbff) {
      const low = value.charCodeAt(index + 1);
      if (!(low >= 0xdc00 && low <= 0xdfff)) return false;
      index += 1;
    } else if (code >= 0xdc00 && code <= 0xdfff) return false;
  }
  return true;
};

interface MetadataEntry {
  readonly key: WaveMetadataKey;
  readonly value: string;
  readonly encoded: Uint8Array;
}

const normalizeMetadata = (metadata: WaveMetadata | undefined): {
  readonly value: WaveMetadata;
  readonly entries: readonly MetadataEntry[];
} => {
  if (metadata === undefined) return { value: Object.freeze({}), entries: Object.freeze([]) };
  if (metadata === null || typeof metadata !== 'object' || Array.isArray(metadata)) {
    fail('METADATA_INVALID', 'metadata must be an object');
  }
  const known = new Set<string>(WAVE_METADATA_KEYS);
  for (const key of Object.keys(metadata)) {
    if (!known.has(key)) fail('METADATA_INVALID', `unknown INFO key ${key}`);
  }
  const output: Partial<Record<WaveMetadataKey, string>> = {};
  const entries: MetadataEntry[] = [];
  for (const key of WAVE_METADATA_KEYS) {
    const value = metadata[key];
    if (value === undefined) continue;
    if (typeof value !== 'string' || value.includes('\0') || !validUnicode(value)) {
      fail('METADATA_INVALID', `${key} must be valid Unicode without NUL`);
    }
    const encoded = utf8.encode(value);
    if (encoded.length > 1_024) fail('METADATA_INVALID', `${key} exceeds 1024 UTF-8 bytes`);
    output[key] = value;
    entries.push(Object.freeze({ key, value, encoded }));
  }
  return { value: Object.freeze(output), entries: Object.freeze(entries) };
};

const metadataPayloadBytes = (entries: readonly MetadataEntry[]): number => {
  if (entries.length === 0) return 0;
  let bytes = 4;
  for (const entry of entries) {
    const valueBytes = entry.encoded.length + 1;
    bytes += 8 + valueBytes + (valueBytes & 1);
  }
  return bytes;
};

const checkedWaveLength = (frames: number, channels: number, entries: readonly MetadataEntry[]): number => {
  const dataBytes = BigInt(frames) * BigInt(channels) * 4n;
  const listPayload = BigInt(metadataPayloadBytes(entries));
  const total = 12n + 24n + 8n + dataBytes + (listPayload === 0n ? 0n : 8n + listPayload + (listPayload & 1n));
  if (dataBytes > 0xffff_ffffn || total - 8n > 0xffff_ffffn || total > BigInt(Number.MAX_SAFE_INTEGER)) {
    fail('RIFF_OVERFLOW', 'classic RIFF/WAVE size exceeds uint32');
  }
  return Number(total);
};

const writeAscii = (bytes: Uint8Array, offset: number, value: string): void => {
  for (let index = 0; index < value.length; index += 1) bytes[offset + index] = value.charCodeAt(index);
};

const encodePreparedWave = (
  samples: Float32Array,
  sampleRate: number,
  channels: number,
  entries: readonly MetadataEntry[],
): Uint8Array => {
  const frames = samples.length / channels;
  const bytes = new Uint8Array(checkedWaveLength(frames, channels, entries));
  const view = new DataView(bytes.buffer);
  writeAscii(bytes, 0, 'RIFF');
  view.setUint32(4, bytes.length - 8, true);
  writeAscii(bytes, 8, 'WAVE');
  writeAscii(bytes, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 3, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channels * 4, true);
  view.setUint16(32, channels * 4, true);
  view.setUint16(34, 32, true);
  let offset = 36;
  if (entries.length > 0) {
    const payloadBytes = metadataPayloadBytes(entries);
    writeAscii(bytes, offset, 'LIST');
    view.setUint32(offset + 4, payloadBytes, true);
    writeAscii(bytes, offset + 8, 'INFO');
    offset += 12;
    for (const entry of entries) {
      writeAscii(bytes, offset, entry.key);
      const valueBytes = entry.encoded.length + 1;
      view.setUint32(offset + 4, valueBytes, true);
      bytes.set(entry.encoded, offset + 8);
      bytes[offset + 8 + entry.encoded.length] = 0;
      offset += 8 + valueBytes + (valueBytes & 1);
    }
  }
  writeAscii(bytes, offset, 'data');
  view.setUint32(offset + 4, samples.byteLength, true);
  offset += 8;
  for (let index = 0; index < samples.length; index += 1) view.setFloat32(offset + index * 4, samples[index]!, true);
  return bytes;
};

export const getFloat32WaveByteLength = (
  frames: number,
  sampleRate: number,
  channels: number,
  metadata: WaveMetadata = {},
): number => {
  if (!Number.isSafeInteger(frames) || frames < 1) fail('SAMPLE_LAYOUT', 'frames must be a positive safe integer');
  assertInteger('sampleRate', sampleRate, MIN_SAMPLE_RATE, MAX_SAMPLE_RATE);
  assertInteger('channels', channels, 1, 8);
  const prepared = normalizeMetadata(metadata);
  return checkedWaveLength(frames, channels, prepared.entries);
};

export const encodeFloat32Wave = (
  samples: Float32Array,
  sampleRate: number,
  channels: number,
  metadata: WaveMetadata = {},
): Uint8Array => {
  assertInteger('sampleRate', sampleRate, MIN_SAMPLE_RATE, MAX_SAMPLE_RATE);
  assertInteger('channels', channels, 1, 8);
  if (!(samples instanceof Float32Array) || samples.length === 0 || samples.length % channels !== 0) {
    fail('SAMPLE_LAYOUT', 'samples must contain a non-empty whole number of interleaved frames');
  }
  const prepared = normalizeMetadata(metadata);
  for (let index = 0; index < samples.length; index += 1) {
    if (!Number.isFinite(samples[index])) {
      const frame = Math.floor(index / channels);
      const channel = index % channels;
      throw new OfflineRenderError('NON_FINITE', `non-finite sample at channel ${channel}, frame ${frame}`, { channel, frame });
    }
  }
  return encodePreparedWave(samples, sampleRate, channels, prepared.entries);
};

const sinc = (value: number): number => value === 0 ? 1 : Math.sin(Math.PI * value) / (Math.PI * value);

/** Fixed 4-phase, 32-tap-per-phase Blackman-windowed sinc coefficients. */
export const TRUE_PEAK_COEFFICIENTS: readonly (readonly number[])[] = Object.freeze(
  Array.from({ length: TRUE_PEAK_OVERSAMPLE }, (_, phase) => {
    const fraction = phase / TRUE_PEAK_OVERSAMPLE;
    const coefficients = Array.from({ length: TRUE_PEAK_TAPS_PER_PHASE }, (_, tapIndex) => {
      const tap = tapIndex - 15;
      const window = 0.42 - 0.5 * Math.cos((2 * Math.PI * tapIndex) / (TRUE_PEAK_TAPS_PER_PHASE - 1))
        + 0.08 * Math.cos((4 * Math.PI * tapIndex) / (TRUE_PEAK_TAPS_PER_PHASE - 1));
      return sinc(fraction - tap) * window;
    });
    const sum = coefficients.reduce((total, coefficient) => total + coefficient, 0);
    return Object.freeze(coefficients.map((coefficient) => coefficient / sum));
  }),
);

const channelTruePeak = (samples: Float32Array, channels: number, channel: number): number => {
  const frames = samples.length / channels;
  let peak = 0;
  for (let frame = 0; frame < frames; frame += 1) {
    for (let phase = 0; phase < TRUE_PEAK_OVERSAMPLE; phase += 1) {
      let interpolated = 0;
      const coefficients = TRUE_PEAK_COEFFICIENTS[phase]!;
      for (let tapIndex = 0; tapIndex < TRUE_PEAK_TAPS_PER_PHASE; tapIndex += 1) {
        const sourceFrame = frame + tapIndex - 15;
        if (sourceFrame < 0 || sourceFrame >= frames) continue;
        const sample = samples[sourceFrame * channels + channel]!;
        if (Number.isFinite(sample)) interpolated += sample * coefficients[tapIndex]!;
      }
      peak = Math.max(peak, Math.abs(interpolated));
    }
  }
  return peak;
};

interface AnalysisAccumulator {
  peak: number;
  absolutePeak: number;
  peakFrame: number;
  squareSum: number;
  sum: number;
  finiteCount: number;
  nonFiniteCount: number;
  overRangeCount: number;
}

const accumulator = (): AnalysisAccumulator => ({
  peak: 0,
  absolutePeak: 0,
  peakFrame: 0,
  squareSum: 0,
  sum: 0,
  finiteCount: 0,
  nonFiniteCount: 0,
  overRangeCount: 0,
});

const accumulate = (state: AnalysisAccumulator, sample: number, frame: number): void => {
  if (!Number.isFinite(sample)) {
    state.nonFiniteCount += 1;
    return;
  }
  const absolute = Math.abs(sample);
  if (absolute > state.absolutePeak) {
    state.absolutePeak = absolute;
    state.peak = sample;
    state.peakFrame = frame;
  }
  state.squareSum += sample * sample;
  state.sum += sample;
  state.finiteCount += 1;
  if (absolute > 1) state.overRangeCount += 1;
};

const finishAnalysis = (state: AnalysisAccumulator, truePeak4x: number): SignalAnalysis => Object.freeze({
  peak: state.peak,
  absolutePeak: state.absolutePeak,
  peakFrame: state.peakFrame,
  rms: state.finiteCount === 0 ? 0 : Math.sqrt(state.squareSum / state.finiteCount),
  dcMean: state.finiteCount === 0 ? 0 : state.sum / state.finiteCount,
  nonFiniteCount: state.nonFiniteCount,
  overRangeCount: state.overRangeCount,
  truePeak4x,
});

export const analyzeInterleaved = (samples: Float32Array, channels: number): RenderAnalysis => {
  assertInteger('channels', channels, 1, 8);
  if (!(samples instanceof Float32Array) || samples.length === 0 || samples.length % channels !== 0) {
    fail('SAMPLE_LAYOUT', 'samples must contain a non-empty whole number of interleaved frames');
  }
  const perChannel = Array.from({ length: channels }, accumulator);
  const aggregate = accumulator();
  for (let index = 0; index < samples.length; index += 1) {
    const frame = Math.floor(index / channels);
    const sample = samples[index]!;
    accumulate(perChannel[index % channels]!, sample, frame);
    accumulate(aggregate, sample, frame);
  }
  const truePeaks = perChannel.map((_state, channel) => channelTruePeak(samples, channels, channel));
  return Object.freeze({
    channels: Object.freeze(perChannel.map((state, channel) => finishAnalysis(state, truePeaks[channel]!))),
    aggregate: finishAnalysis(aggregate, Math.max(...truePeaks)),
  });
};

const validateRequest = (request: OfflineRenderRequest): {
  readonly metadata: ReturnType<typeof normalizeMetadata>;
  readonly maximumRenderedFrames: number;
  readonly exportedFrames: number;
  readonly tailDecision: TailDecision;
  readonly loopDecision: Readonly<LoopOptions> | null;
} => {
  if (request === null || typeof request !== 'object') fail('INVALID_REQUEST', 'request must be an object');
  assertInteger('sampleRate', request.sampleRate, MIN_SAMPLE_RATE, MAX_SAMPLE_RATE);
  assertInteger('channels', request.channels, 1, 8);
  assertInteger('sourceFrames', request.sourceFrames, 1, MAX_SOURCE_FRAMES);
  assertInteger('blockSize', request.blockSize, MIN_BLOCK_SIZE, MAX_BLOCK_SIZE);
  if (typeof request.renderBlock !== 'function') fail('INVALID_REQUEST', 'renderBlock must be a function');
  if (request.isCancelled !== undefined && typeof request.isCancelled !== 'function') {
    fail('INVALID_REQUEST', 'isCancelled must be a function');
  }
  if (request.tail !== undefined && request.loop !== undefined) fail('TAIL_LOOP_CONFLICT', 'tail and loop are mutually exclusive');

  let maximumRenderedFrames = request.sourceFrames;
  let exportedFrames = request.sourceFrames;
  let tailDecision: TailDecision = Object.freeze({ mode: 'none' });
  if (request.tail !== undefined) {
    if (request.tail.mode === 'exact') {
      assertInteger('tail.frames', request.tail.frames, 0, MAX_TAIL_FRAMES);
      maximumRenderedFrames += request.tail.frames;
      exportedFrames = maximumRenderedFrames;
      tailDecision = Object.freeze({ mode: 'exact', requestedFrames: request.tail.frames });
    } else if (request.tail.mode === 'automatic') {
      if (!Number.isFinite(request.tail.thresholdDbfs)
        || request.tail.thresholdDbfs < -120 || request.tail.thresholdDbfs > -20) {
        fail('INVALID_REQUEST', 'tail.thresholdDbfs must be in [-120, -20]');
      }
      assertInteger('tail.holdFrames', request.tail.holdFrames, 1, MAX_BLOCK_SIZE);
      assertInteger('tail.maxFrames', request.tail.maxFrames, 0, MAX_TAIL_FRAMES);
      maximumRenderedFrames += request.tail.maxFrames;
      exportedFrames = maximumRenderedFrames;
      tailDecision = Object.freeze({
        mode: 'automatic',
        thresholdDbfs: request.tail.thresholdDbfs,
        thresholdLinear: 10 ** (request.tail.thresholdDbfs / 20),
        holdFrames: request.tail.holdFrames,
        maxFrames: request.tail.maxFrames,
      });
    } else fail('INVALID_REQUEST', 'unknown tail mode');
  }

  let loopDecision: Readonly<LoopOptions> | null = null;
  if (request.loop !== undefined) {
    assertInteger('loop.startFrame', request.loop.startFrame, 0, request.sourceFrames - 1);
    assertInteger('loop.endFrame', request.loop.endFrame, 1, request.sourceFrames);
    if (request.loop.endFrame <= request.loop.startFrame) fail('INVALID_REQUEST', 'loop range must be non-empty');
    const loopLength = request.loop.endFrame - request.loop.startFrame;
    assertInteger('loop.crossfadeFrames', request.loop.crossfadeFrames, 0, Math.min(MAX_BLOCK_SIZE, Math.floor(loopLength / 2)));
    exportedFrames = loopLength;
    loopDecision = Object.freeze({ ...request.loop });
  }

  const metadata = normalizeMetadata(request.metadata);
  checkedWaveLength(exportedFrames, request.channels, metadata.entries);
  return { metadata, maximumRenderedFrames, exportedFrames, tailDecision, loopDecision };
};

const applyLoop = (source: Float32Array, channels: number, loop: Readonly<LoopOptions>): Float32Array => {
  const loopFrames = loop.endFrame - loop.startFrame;
  const output = source.slice(loop.startFrame * channels, loop.endFrame * channels);
  const crossfade = loop.crossfadeFrames;
  if (crossfade === 0) return output;
  for (let index = 0; index < crossfade; index += 1) {
    const theta = crossfade === 1 ? Math.PI / 4 : (index / (crossfade - 1)) * Math.PI / 2;
    const endGain = Math.cos(theta);
    const startGain = Math.sin(theta);
    for (let channel = 0; channel < channels; channel += 1) {
      const destinationFrame = loopFrames - crossfade + index;
      const endSample = source[(loop.endFrame - crossfade + index) * channels + channel]!;
      const startSample = source[(loop.startFrame + index) * channels + channel]!;
      output[destinationFrame * channels + channel] = Math.fround(endSample * endGain + startSample * startGain);
    }
  }
  return output;
};

const fnv1a64 = (bytes: Uint8Array): string => {
  let hash = 0xcbf2_9ce4_8422_2325n;
  for (const byte of bytes) {
    hash ^= BigInt(byte);
    hash = BigInt.asUintN(64, hash * 0x100_0000_01b3n);
  }
  return hash.toString(16).padStart(16, '0');
};

export const renderOffline = (request: OfflineRenderRequest): OfflineRenderResult => {
  const validated = validateRequest(request);
  // Caller code runs inside renderBlock/isCancelled, so retain only this validated
  // snapshot after validation rather than re-reading a mutable request object.
  const { sampleRate, channels, sourceFrames, blockSize, renderBlock, isCancelled } = request;
  const buffers = Object.freeze(Array.from({ length: channels }, () => new Float32Array(blockSize)));
  const chunks: Float32Array[] = [];
  let callbackCount = 0;
  let startFrame = 0;
  let retainedFrames = 0;
  let belowThresholdFrames = 0;
  let automaticComplete = false;
  const automatic = validated.tailDecision.mode === 'automatic' ? validated.tailDecision : null;

  while (startFrame < validated.maximumRenderedFrames && !automaticComplete) {
    if (isCancelled?.()) fail('CANCELLED', `cancelled before block at frame ${startFrame}`);
    const frames = Math.min(blockSize, validated.maximumRenderedFrames - startFrame);
    for (const channel of buffers) channel.fill(0);
    try {
      renderBlock(Object.freeze({
        startFrame,
        frames,
        sampleRate,
        outputs: buffers,
      }));
    } catch (cause) {
      throw new OfflineRenderError('CALLBACK_FAILED', `render callback failed at frame ${startFrame}`, { startFrame, cause });
    }
    callbackCount += 1;
    const interleaved = new Float32Array(frames * channels);
    let framesToRetain = frames;
    for (let offset = 0; offset < frames; offset += 1) {
      let allBelow = true;
      for (let channel = 0; channel < channels; channel += 1) {
        const sample = buffers[channel]![offset]!;
        const frame = startFrame + offset;
        if (!Number.isFinite(sample)) {
          throw new OfflineRenderError('NON_FINITE', `non-finite sample at channel ${channel}, frame ${frame}`, { channel, frame });
        }
        interleaved[offset * channels + channel] = sample;
        if (automatic && frame >= sourceFrames && Math.abs(sample) >= automatic.thresholdLinear) allBelow = false;
      }
      const frame = startFrame + offset;
      if (automatic && frame >= sourceFrames) {
        belowThresholdFrames = allBelow ? belowThresholdFrames + 1 : 0;
        if (belowThresholdFrames >= automatic.holdFrames) {
          framesToRetain = offset + 1;
          automaticComplete = true;
          break;
        }
      }
    }
    chunks.push(framesToRetain === frames ? interleaved : interleaved.slice(0, framesToRetain * channels));
    retainedFrames += framesToRetain;
    startFrame += frames;
  }

  if (automatic && !automaticComplete) fail('TAIL_LIMIT', `silence hold not reached within ${automatic.maxFrames} tail frames`);
  const rendered = new Float32Array(retainedFrames * channels);
  let sampleOffset = 0;
  for (const chunk of chunks) {
    rendered.set(chunk, sampleOffset);
    sampleOffset += chunk.length;
  }
  const samples = validated.loopDecision ? applyLoop(rendered, channels, validated.loopDecision) : rendered;
  const totalFrames = samples.length / channels;
  const tailFrames = validated.loopDecision ? 0 : retainedFrames - sourceFrames;
  const wavBytes = encodePreparedWave(samples, sampleRate, channels, validated.metadata.entries);
  return Object.freeze({
    sampleRate,
    channels,
    sourceFrames,
    tailFrames,
    totalFrames,
    blockSize,
    loopDecision: validated.loopDecision,
    tailDecision: validated.tailDecision,
    callbackCount,
    samples,
    wavBytes,
    contentHash: fnv1a64(wavBytes),
    analysis: analyzeInterleaved(samples, channels),
    metadata: validated.metadata.value,
  });
};

class OfflineRendererModuleInstance implements DspModuleInstance {
  private readonly maxBlockSize: number;
  constructor(maxBlockSize: number) { this.maxBlockSize = maxBlockSize; }
  reset(): void {}
  setParameter(id: string, _value: number): void { throw new RangeError(`unknown parameter: ${id}`); }
  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) {
      throw new RangeError('frames exceeds the configured maximum block size');
    }
  }
}

/** Catalog descriptor; rendering is exposed by the synchronous renderOffline API. */
export const mod036 = defineDspModule({
  descriptor: {
    id: MODULE_ID,
    name: 'Offline renderer / export / analyzer',
    version: MODULE_VERSION,
    inputs: [],
    outputs: [],
    parameters: [],
  },
  createInstance(context) {
    if (!(context.sampleRate >= MIN_SAMPLE_RATE && context.sampleRate <= MAX_SAMPLE_RATE)
      || !Number.isInteger(context.maxBlockSize)
      || context.maxBlockSize < MIN_BLOCK_SIZE
      || context.maxBlockSize > MAX_BLOCK_SIZE) {
      throw new RangeError('invalid DSP instance context');
    }
    return new OfflineRendererModuleInstance(context.maxBlockSize);
  },
});
